import fetchLoyaltyActionRewards from 'actions/loyalty/fetchLoyaltyActionRewards'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import {
  isSupportedLoyaltyActionReward,
  SupportedActionReward,
  type SupportedLoyaltyActionReward,
} from 'loyalty/constants/loyaltyActionRewards'
import useLuxPlusSubscriptionProduct from 'luxPlus/hooks/useLuxPlusSubscriptionProduct'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getHasPaidLuxPlusMembershipInCurrentRegion } from 'luxPlus/selectors/membership'
import { useEffect, useMemo } from 'react'

const ACTION_REWARD_CAN_SHOW: Partial<Record<SupportedActionReward, (state: App.State) => boolean>> = {
  [SupportedActionReward.LUX_PLUS_RENEWAL]: getHasPaidLuxPlusMembershipInCurrentRegion,
  [SupportedActionReward.LUX_PLUS_BACKUP_PAYMENT_METHOD_ADDED]: getHasPaidLuxPlusMembershipInCurrentRegion,
}

function useLoyaltyActionRewards() {
  const dispatch = useAppDispatch()
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const subscriptionOffers = useAppSelector(state => state.luxPlus.products.subscription.offers)
  const subscriptionError = useAppSelector(state => state.luxPlus.products.subscription.error)

  // Hidden dependency: fetchLoyaltyActionRewards does not load the LuxPlus
  // catalog, but LuxPlusRenewal earn metadata reads it from Redux
  // (getLoyaltyActionRewardEarnRequest). Wait until offers/error are present.
  const subscriptionCatalogReady = !isLuxPlusEnabled || !!subscriptionOffers || !!subscriptionError

  const actionRewards = useAppSelector(state => state.loyalty.actionRewards)
  const rewards = useAppSelector((state): Array<SupportedLoyaltyActionReward> => {
    const data = state.loyalty.actionRewards.data
    if (!data) return EmptyArray

    return data.filter((reward): reward is SupportedLoyaltyActionReward => {
      if (!isSupportedLoyaltyActionReward(reward)) return false
      return ACTION_REWARD_CAN_SHOW[reward.actionName]?.(state) ?? true
    })
  }, shallowEqual)

  useLuxPlusSubscriptionProduct(isLuxPlusEnabled)

  useEffect(() => {
    if (!subscriptionCatalogReady) return
    dispatch(fetchLoyaltyActionRewards())
  }, [dispatch, subscriptionCatalogReady])

  // Keep existing data during refetch so the section does not flash the loading skeleton.
  const isInitialLoading = !actionRewards.data && (
    !subscriptionCatalogReady || !!actionRewards.fetching || !!actionRewards.initial
  )

  return useMemo(() => ({
    rewards,
    isInitialLoading,
  }), [isInitialLoading, rewards])
}

export default useLoyaltyActionRewards
